import type { ReactNode } from "react";
import { Button } from "./button";
import { EmptyState } from "./empty-state";
import { Skeleton } from "./skeleton";

/**
 * Mandatory five-state wrapper for every async view.
 * States: idle | loading | empty | error | partial | success
 *
 * - idle:    not yet requested (rare — most views fetch on mount)
 * - loading: skeleton
 * - empty:   success with zero items → CTA
 * - error:   retry + preserved input
 * - partial: some data + stale marker
 * - success: render children
 */
type Status = "idle" | "loading" | "pending" | "error" | "success";

export interface StateMatrixProps<T> {
  status: Status;
  isFetching?: boolean;
  data: T | undefined;
  isEmpty?: (data: T) => boolean;
  error?: unknown;
  onRetry?: () => void;
  loading?: ReactNode;
  empty?: ReactNode;
  children: (data: T) => ReactNode;
}

export function StateMatrix<T>({
  status,
  isFetching,
  data,
  isEmpty,
  error,
  onRetry,
  loading,
  empty,
  children,
}: StateMatrixProps<T>) {
  if (status === "idle") return null;

  if (status === "loading" || status === "pending") {
    return (
      <div aria-busy="true" aria-live="polite">
        {loading ?? <DefaultSkeleton />}
      </div>
    );
  }

  if (status === "error") {
    const message =
      error instanceof Error ? error.message : typeof error === "string" ? error : "Request failed.";
    return (
      <div className="flex flex-col items-start gap-3 rounded-md border border-[var(--danger)]/40 bg-[var(--danger)]/5 p-4">
        <div>
          <p className="text-sm font-medium text-[var(--danger)]">Something went wrong</p>
          <p className="text-xs text-[var(--fg-muted)]">{message}</p>
        </div>
        {onRetry && (
          <Button size="sm" variant="secondary" onClick={onRetry}>
            Try again
          </Button>
        )}
      </div>
    );
  }

  // success
  if (data === undefined) return null;

  if (isEmpty?.(data)) {
    return <>{empty ?? <EmptyState title="Nothing here yet" />}</>;
  }

  return (
    <div aria-busy={isFetching ? "true" : undefined} data-partial={isFetching ? "" : undefined}>
      {children(data)}
    </div>
  );
}

function DefaultSkeleton() {
  return (
    <div className="flex flex-col gap-2">
      <Skeleton className="h-5 w-1/3" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-5/6" />
      <Skeleton className="h-4 w-2/3" />
    </div>
  );
}
